import { useCallback, useEffect } from 'react';
import { Linking } from 'react-native';
import { UnifiedDeepLinkData } from 'react-native-appsflyer';

import { useLinkTo } from '@react-navigation/native';

import { handleBrazeInitialUrl } from '@/data/braze';
import { isPartnerLink } from '@/feature/partner-deeplink/utils/partner-link-import-utils';
import { Product, useJurisdictionStore } from '@/hooks/use-jurisdiction';
import { isOneLink } from '@/hooks/use-one-link-navigation';
import { read, remove } from '@/utils/async-storage';
import { isWeb } from '@/utils/constants-platform-specific';
import { requiresManualWebHandling } from '@/utils/linking';
import { logger } from '@/utils/logging';
import { create } from 'zustand';

import { SocialLink, isSocialShareLink } from '../utils/social-link';

const LOG_TAG = '[Linking]';

// carried across a product switch so the destination importer doesn't re-fetch; unlike
// pendingLink, which handleLink consumes and clears
export type CrossProductImport = {
    shortId: string;
    socialLink: SocialLink;
    product: Product;
};

interface LinkingStore {
    oneLinkData?: UnifiedDeepLinkData;
    pendingLink?: string;
    initialLinkHandledSources: string[];
    unavailableProductRedirect?: Product;
    crossProductImport?: CrossProductImport;
    setOneLinkData: (data: UnifiedDeepLinkData | undefined) => void;
    setPendingLink: (path: string | undefined) => void;
    clearHandledSources: () => void;
    setInitialLinkDelivered: (source: string) => void;
    setUnavailableProductRedirect: (product: Product | undefined) => void;
    setCrossProductImport: (importPayload: CrossProductImport | undefined) => void;
}

export const useLinkingStore = create<LinkingStore>()((set, get) => ({
    initialLinkHandledSources: [],
    pendingLink: undefined,
    unavailableProductRedirect: undefined,
    setOneLinkData: (data: UnifiedDeepLinkData | undefined) => {
        set({ oneLinkData: data });
    },
    setPendingLink(path) {
        set({ pendingLink: path });
    },
    setInitialLinkDelivered: (source: string) => {
        set({ initialLinkHandledSources: [...get().initialLinkHandledSources, source] });
    },
    clearHandledSources: () => {
        set({ initialLinkHandledSources: [] });
    },
    setUnavailableProductRedirect: (product: Product | undefined) => {
        set({ unavailableProductRedirect: product });
    },
    setCrossProductImport: (importPayload: CrossProductImport | undefined) => {
        set({ crossProductImport: importPayload });
    },
}));

// drops a hand-off the destination can no longer take; true when one was parked for that product
export const discardCrossProductImport = (product: Product) => {
    const { crossProductImport, setCrossProductImport } = useLinkingStore.getState();
    if (crossProductImport?.product !== product) {
        return false;
    }
    setCrossProductImport(undefined);
    return true;
};

export const usePendingSocialLink = (product: Product, onImport: (importPayload: CrossProductImport) => void) => {
    const activeProduct = useJurisdictionStore(state => state.product);
    const crossProductImport = useLinkingStore(state => state.crossProductImport);

    useEffect(() => {
        if (crossProductImport?.product === product && activeProduct === product) {
            useLinkingStore.getState().setCrossProductImport(undefined);
            onImport(crossProductImport);
        }
    }, [activeProduct, crossProductImport, onImport, product]);
};

const shouldHandleLink = (url: string) => {
    if (isSocialShareLink(url)) {
        return true;
    } else if (isPartnerLink(url)) {
        return true;
    } else if (isOneLink(url)) {
        return true;
    } else {
        return false;
    }
};

/**
 * Extracts the path to navigate to from a deep link on either web or mobile.
 */
const getNavigationPathFromDeepLink = (deeplink: string) => {
    if (isWeb) {
        // deep_link_value is a param set on appsflyer links which tells us where to navigate to when a user click a deep link to the app
        // so we search for it and return it if it exists
        if (deeplink.includes('deep_link_value')) {
            const urlObject = new URL(deeplink);
            return urlObject.searchParams.get('deep_link_value') ?? '';
        }

        if (deeplink.includes('entry/')) {
            return deeplink.substring(deeplink.indexOf('entry/'));
        }

        return deeplink;
    } else {
        return deeplink.split('://').pop();
    }
};

/**
 * Custom hook for handling deep linking functionality. This ensures that the initial link
 * is delivered only once per source and that new links are delivered as they come.
 *
 * Uses a Zustand store to store that the initial link has been handled, so when this hook gets
 * re-mounted the `onInitialLink` callback is not called again.
 *
 * @param onInitialLink - Callback function to handle the initial link.
 * @param onNewLink - Callback function to handle new links.
 * @param source - A string to identify the source where the initial link is being handled. This is necessary because we want to make sure `onInitialLink()` is only delivered once per source, and without a source the link can be consumed by one source, and then not be available for another source. If not passing a source, 'default' will be used, which will result in `onInitialLink()` not being called in the case that another component already used it.
 * @param product - When set, only deliver while this product is active; navigators stack, so an inactive one must stay silent.
 */
export const useLinking = ({
    source = 'default',
    product,
    onInitialLink,
    onNewLink,
}: {
    source?: string;
    product?: Product;
    onInitialLink: (link: string) => void;
    onNewLink: (link: string) => void;
}) => {
    const initialLinkHandledSources = useLinkingStore(state => state.initialLinkHandledSources);
    const initialLinkHandled = initialLinkHandledSources.includes(source);
    const pendingLink = useLinkingStore(state => state.pendingLink);
    const setPendingLink = useLinkingStore(state => state.setPendingLink);
    const setInitialLinkDelivered = useLinkingStore(state => state.setInitialLinkDelivered);
    const linkTo = useLinkTo();

    const isProductActive = useCallback(
        () => !product || useJurisdictionStore.getState().product === product,
        [product]
    );

    const handleLinkTo = useCallback(
        (path: string) => {
            try {
                linkTo('/' + path);
            } catch (e) {
                // if the link is not valid, we just ignore it as we're already on the lobby
                logger.info(`Error linking to ${path}`, e);
            }
        },
        [linkTo]
    );

    const handleLink = useCallback(
        async (url: string | null) => {
            if (url || pendingLink) {
                logger.info(LOG_TAG, 'Incoming link', url, pendingLink);
            }
            if (isWeb) {
                const webPendingLink = await read('webPendingLink');
                // pendingLink is used when the use is logged out and tries to deep link into the app
                // webPendingLink is stored in the async storage when the user is logged out and tries to deep link into the web app

                if (url || webPendingLink) {
                    // on web, we get the full url including the prefix so we have to extract the path / entry id
                    const path = getNavigationPathFromDeepLink(webPendingLink ?? url);
                    if (!path) {
                        return;
                    }
                    if (requiresManualWebHandling(path)) {
                        if (!isProductActive()) {
                            return;
                        }
                        onInitialLink(path);
                    } else {
                        handleLinkTo('/' + path);
                    }
                    setInitialLinkDelivered(source);
                    remove('webPendingLink');
                }
                return;
            }
            const urlToHandle = url ?? pendingLink;
            if (urlToHandle) {
                if (shouldHandleLink(urlToHandle)) {
                    if (!isProductActive()) {
                        return;
                    }
                    onInitialLink(urlToHandle);
                } else {
                    const path = getNavigationPathFromDeepLink(urlToHandle);
                    // let the navigation handle the nevigation to the path
                    if (path) {
                        handleLinkTo(path);
                    }
                }
                setInitialLinkDelivered(source);
                setPendingLink(undefined);
            }
        },
        [handleLinkTo, isProductActive, onInitialLink, pendingLink, setInitialLinkDelivered, setPendingLink, source]
    );

    useEffect(() => {
        if (!initialLinkHandled) {
            Linking.getInitialURL().then(async url => await handleLink(url));
        }
    }, [handleLink, initialLinkHandled]);

    useEffect(() => {
        // as per braze docs, we need to call this in order to handle links when the app is closed
        // iOS never clears initialPushPayload, so without the same guard as above it replays forever
        if (!initialLinkHandled) {
            handleBrazeInitialUrl(async pushPayload => await handleLink(pushPayload?.url || null));
        }
    }, [handleLink, initialLinkHandled]);

    useEffect(() => {
        const sub = Linking.addEventListener('url', event => {
            if (event.url && isProductActive()) {
                onNewLink(event.url);
            }
        });
        return sub.remove;
    }, [isProductActive, onNewLink]);
};
